import React, { useMemo, useCallback } from 'react';
import { Breadcrumbs, Breadcrumb } from '@moda/om';
import { useLocation } from 'react-router-dom';
import { omit } from 'ramda';
import { FILTER_KEYS } from '../../hooks/usePLPParams';
import { useUrlParams } from '../../hooks/useUrlParams';
import { findRouteFor, hrefFor } from '../../routers';
import { titleize } from '../../lib/titleize';

import './PLPBreadcrumbs.scss';

const VERTICALS = {
  women: {
    href: hrefFor.HomePage()
  },
  men: {
    href: hrefFor.HomePageMen({ vertical: 'men' })
  }
};

export const PLPBreadcrumbs = () => {
  const { getHrefFor, params } = useUrlParams();
  const location = useLocation();
  const route = findRouteFor(location.pathname);
  const vertical = Array.isArray(params.vertical) ? params.vertical[0] : params.vertical;
  const category = Array.isArray(params.category) ? params.category[0] : params.category;
  const subcategory = Array.isArray(params.subcategory)
    ? params.subcategory[0]
    : params.subcategory;

  const getVerticalHref = useCallback(() => {
    try {
      return getHrefFor({
        ...omit(['category', 'subcategory', ...FILTER_KEYS], params)
      });
    } catch {
      return VERTICALS[vertical as 'women' | 'men'].href;
    }
  }, [params, getHrefFor, vertical]);

  const getCategoryHref = useCallback(() => {
    try {
      return getHrefFor({
        ...omit(['subcategory', ...FILTER_KEYS], params)
      });
    } catch {
      return '';
    }
  }, [params, getHrefFor]);

  const getBaseHref = useCallback(() => {
    try {
      return getHrefFor({
        ...omit(['vertical', 'category', 'subcategory', ...FILTER_KEYS], params)
      });
    } catch {
      return '';
    }
  }, [params, getHrefFor]);

  const firstBreadcrumbTitle = useMemo(() => {
    if (route?.key === 'ProductCategoryListingPage') {
      if (params.section) {
        return `All ${params.section}`;
      }
    }
    if (route?.key === 'DesignerPage') {
      return params.designerSlug as string;
    }
    if (route?.key === 'FavoritesPage') {
      return 'All Favorites';
    }
    if (
      route?.key &&
      ['GuidePage', 'ProductSearchListingPage', 'DesignersIndexPage'].includes(route?.key)
    ) {
      return 'All Results';
    }
  }, [route, params]);

  return (
    <Breadcrumbs className="PLPBreadcrumbs">
      {firstBreadcrumbTitle && (
        <Breadcrumb to={vertical ? getBaseHref() : undefined}>
          {titleize(firstBreadcrumbTitle)}
        </Breadcrumb>
      )}

      {vertical && (
        <Breadcrumb to={category ? getVerticalHref() : undefined}>{titleize(vertical)}</Breadcrumb>
      )}

      {category && (
        <Breadcrumb to={subcategory ? getCategoryHref() : undefined}>
          {titleize(category)}
        </Breadcrumb>
      )}

      {subcategory && <Breadcrumb>{titleize(subcategory)}</Breadcrumb>}
    </Breadcrumbs>
  );
};
